import { motion } from "framer-motion";
import { MessageCircle, Repeat2, Heart, BadgeCheck, Play } from "lucide-react";
import { TWEETS } from "../constants";
import { XIcon } from "./XIcon";
import { TweetEmbed } from "./TweetEmbed";
import { SmartImage } from "./SmartImage";

const TweetCard = ({ t, i }) => (
  <motion.a
    href={t.url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`tweet-card-${i + 1}`}
    initial={{ opacity: 0, y: 40, rotate: i % 2 ? 1.5 : -1.5 }}
    whileInView={{ opacity: 1, y: 0, rotate: i % 2 ? 0.8 : -0.8 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay: (i % 3) * 0.12, type: "spring" }}
    className="neon-card p-5 flex flex-col text-left no-underline break-inside-avoid mb-6 lg:mb-8"
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <img src={t.avatar} alt={t.name} loading="lazy" decoding="async" className="w-11 h-11 rounded-full border border-[#9945FF]/40" />
        <div>
          <p className="text-white font-semibold flex items-center gap-1 text-sm">
            {t.name} <BadgeCheck size={15} className="text-[#1d9bf0]" />
          </p>
          <p className="text-zinc-500 text-sm">{t.handle}</p>
        </div>
      </div>
      <XIcon size={18} className="text-zinc-500" />
    </div>

    <p className="text-zinc-200 whitespace-pre-line leading-relaxed mt-4 text-[15px] flex-1">{t.text}</p>

    {t.media && (
      <div className="relative mt-4 rounded-xl overflow-hidden border border-[#9945FF]/25">
        <SmartImage src={t.media} alt="tweet media" className="w-full max-h-64 object-cover" />
        {t.video && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="bg-[#14F195] rounded-full p-4 shadow-[0_0_30px_rgba(20,241,149,0.6)]">
              <Play size={22} className="text-[#050409] fill-[#050409]" />
            </span>
          </span>
        )}
      </div>
    )}

    {t.note && (
      <p className="font-marker text-[#14F195] mt-4 -rotate-1">↑ {t.note} ↑</p>
    )}

    <div className="flex items-center gap-6 mt-4 pt-3 border-t border-[#9945FF]/20 text-zinc-500 text-sm">
      <span className="flex items-center gap-1.5"><MessageCircle size={15} /> {t.replies}</span>
      <span className="flex items-center gap-1.5"><Repeat2 size={16} /> {t.reposts}</span>
      <span className="flex items-center gap-1.5 hover:text-pink-500 transition-colors"><Heart size={15} /> {t.likes}</span>
      <span className="ml-auto text-xs">{t.date}</span>
    </div>
  </motion.a>
);

export const Tweets = () => (
  <section id="bull-posts" className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 sm:py-28">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="mb-12"
    >
      <p className="font-bebas tracking-[0.35em] text-[#14F195] text-lg">STRAIGHT FROM THE TIMELINE</p>
      <h2 data-testid="tweets-heading" className="font-marker text-4xl sm:text-5xl gradient-sol text-glow-green mt-2 -rotate-1 inline-block">
        THE TIMELINE KNOWS
      </h2>
    </motion.div>
    <div className="columns-1 md:columns-2 lg:columns-3 gap-6 lg:gap-8">
      {TWEETS.map((t, i) =>
        t.embed ? (
          <TweetEmbed key={t.id} url={t.url} testId={`tweet-embed-${i + 1}`} />
        ) : (
          <TweetCard key={t.id} t={t} i={i} />
        )
      )}
    </div>
  </section>
);
